import React from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components'
import ReactTooltip from 'react-tooltip'
import { pxToRem, color } from '../../utils'
import { cellState, pairKey } from './gridUtils'
import { ink, CHECK_ICON, ALERT_ICON, QUESTION_ICON } from './gridStyles'

const TOOLTIP_ID = 'incompat-grid'

// Altura do cabeçalho vertical. Ela acompanha o maior rótulo (ver `headerHeight`): fixa,
// sobrava com nomes curtos e cortava os longos. Vai numa custom property porque os três
// seletores que dependem dela (canto, coluna e rótulo) são componentes diferentes - e a
// matriz não é oferecida no IE11, o único navegador do projeto sem suporte a var().
const HEADER_HEIGHT_MAX = 240
const HEADER_HEIGHT_MIN = 64

// Numa tela baixa o cabeçalho comeria metade da matriz visível, então lá ele tem um
// teto menor - o nome inteiro continua no tooltip e na linha correspondente.
const HEADER_HEIGHT_SHORT = 130

// O rótulo ocupa DUAS linhas verticais, daí a metade da largura do texto - mas a linha
// nunca fica menor que a maior PALAVRA, que não se parte: "4. METANOL" quebra em "4." e
// "METANOL", e é o segundo que dita a altura.
//
// A largura é MEDIDA, não estimada por número de caracteres: os nomes do cadastro
// misturam caixa alta e baixa, e a diferença entre "METANOL" e "metanol" na mesma
// contagem de letras é o que fazia o rótulo ser cortado.
const LABEL_LINE_HEIGHT = 13
const LABEL_FONT = '600 11px "Open Sans", sans-serif'
const FALLBACK_CHAR_WIDTH = 7.4

let measureContext

const textWidth = text => {
  if (measureContext === undefined) {
    const canvas = typeof document !== 'undefined' && document.createElement('canvas')

    measureContext = (canvas && canvas.getContext && canvas.getContext('2d')) || null

    if (measureContext) measureContext.font = LABEL_FONT
  }

  // jsdom não implementa canvas: nos testes cai na estimativa por caractere.
  return measureContext
    ? measureContext.measureText(text).width
    : text.length * FALLBACK_CHAR_WIDTH
}

const headerHeight = products => {
  const medida = products.reduce((maximo, product, index) => {
    const rotulo = `${index + 1}. ${product.name || ''}`
    const maiorPalavra = rotulo
      .split(/\s+/)
      .reduce((maior, palavra) => Math.max(maior, textWidth(palavra)), 0)

    return Math.max(maximo, textWidth(rotulo) / 2, maiorPalavra)
  }, 0)

  return Math.min(
    Math.max(Math.ceil(medida) + 18, HEADER_HEIGHT_MIN),
    HEADER_HEIGHT_MAX
  )
}

// A matriz ocupa a altura que sobra no modal (ver IncompatibilityGridModal#Content),
// em vez de um teto fixo em px: é o que faz o modal caber exatamente na janela.
// `align-self` mantém o quadro estreito quando há poucos produtos, em vez de esticá-lo
// pela largura toda; `max-width` devolve a rolagem quando há muitos.
const Scroller = styled.div`
  flex: 1 1 auto;
  min-height: ${pxToRem(180)};
  align-self: flex-start;
  max-width: 100%;
  overflow: auto;
  position: relative;
  background: #fff;
  border: 1px solid ${ink.line};
  border-radius: ${pxToRem('7px')};
  -webkit-overflow-scrolling: touch;
`

// border-collapse PRECISA ser separate: com collapse o navegador pinta a borda na
// tabela, não na célula, e as bordas das células sticky "ficam para trás" no scroll.
// O border-spacing dá o respiro que separa os estados sem exigir borda em cada célula.
const Grid = styled.table`
  --exo-head-height: ${({ headHeight }) => pxToRem(headHeight)};

  @media (max-height: 820px) {
    --exo-head-height: ${({ headHeight }) => pxToRem(Math.min(headHeight, HEADER_HEIGHT_SHORT))};
  }

  /* table-layout: fixed com largura automática faz a tabela esticar até o container e
     sobrar coluna vazia; o 1px vira a soma das colunas declaradas. */
  width: 1px;
  border-collapse: separate;
  border-spacing: ${pxToRem(2)};
  table-layout: fixed;
  font-family: 'Open Sans', sans-serif;
  background: #fff;

  tbody tr:hover > th {
    background: ${ink.headHover};
  }

  /* A célula é um <td> nativo com classe estática, não um styled-component por célula:
     numa matriz de 260x260 são 67.600 células, e 67.600 componentes React a mais é a
     diferença entre a matriz abrir e o navegador travar. */
  td {
    width: ${pxToRem(30)};
    min-width: ${pxToRem(30)};
    height: ${pxToRem(26)};
    border-radius: ${pxToRem('4px')};
    background-repeat: no-repeat;
    background-position: center;
  }

  td.cell-ok {
    background-color: ${ink.okBg};
    background-image: ${CHECK_ICON};
    background-size: ${pxToRem(12)} ${pxToRem(12)};
  }

  td.cell-bad,
  td.cell-stale {
    background-size: ${pxToRem(14)} ${pxToRem(14)};
    cursor: help;
    transition: box-shadow 0.12s ease;
  }

  td.cell-bad {
    background-color: ${color.danger};
    background-image: ${ALERT_ICON};
  }

  td.cell-stale {
    background-color: ${color.warning};
    background-image: ${QUESTION_ICON};
  }

  td.cell-bad:hover,
  td.cell-stale:hover {
    outline: none;
    box-shadow: 0 0 0 ${pxToRem(2)} rgba(51, 66, 75, 0.5);
  }
`

const CELL_CLASS = {
  ok: 'cell-ok',
  bad: 'cell-bad',
  stale: 'cell-stale'
}

// Cabeçalho de linha e de coluna compartilham fundo, cor e peso: são o mesmo eixo,
// lido nas duas direções.
const headBase = `
  background: ${ink.head};
  color: ${ink.strong};
  font-size: ${pxToRem(11)};
  font-weight: 600;
  border-radius: ${pxToRem('4px')};
`

const CornerTh = styled.th`
  ${headBase} position: sticky;
  top: 0;
  left: 0;
  z-index: 3;
  text-align: left;
  vertical-align: bottom;
  text-transform: uppercase;
  letter-spacing: ${pxToRem('0.6px')};
  width: ${pxToRem(260)};
  min-width: ${pxToRem(260)};
  height: var(--exo-head-height);
  padding: ${pxToRem(8)};
  box-shadow: ${pxToRem(3)} 0 0 #fff, 0 ${pxToRem(3)} 0 #fff;
`

const ColTh = styled.th`
  ${headBase} position: sticky;
  top: 0;
  z-index: 2;
  vertical-align: bottom;
  width: ${pxToRem(30)};
  min-width: ${pxToRem(30)};
  height: var(--exo-head-height);
  padding: ${pxToRem(6)} 0;
  cursor: help;
  box-shadow: 0 ${pxToRem(3)} 0 #fff;
`

// O nome do produto também identifica a COLUNA, como na matriz de referência: só o
// número obrigava a subir até a linha homônima para saber o que estava sendo cruzado.
// Vertical porque a coluna tem 30px - `writing-mode` + rotate(180deg) faz o texto ser
// lido de baixo para cima. Sem fallback de IE11 de propósito: a grade não é oferecida
// naquele navegador (ver IncompatibilityGridModal#gridAvailable).
const ColLabel = styled.div`
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  max-height: calc(var(--exo-head-height) - ${pxToRem(12)});
  /* duas linhas: em writing-mode vertical elas se empilham na largura da coluna */
  max-width: ${pxToRem(2 * LABEL_LINE_HEIGHT)};
  line-height: ${pxToRem(LABEL_LINE_HEIGHT)};
  margin: 0 auto;
  overflow: hidden;
  white-space: normal;
  word-break: break-word;
`

const RowTh = styled.th`
  ${headBase} position: sticky;
  left: 0;
  z-index: 1;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: ${pxToRem(260)};
  min-width: ${pxToRem(260)};
  padding: ${pxToRem(5)} ${pxToRem(8)};
  box-shadow: ${pxToRem(3)} 0 0 #fff;
  transition: background 0.12s ease;
`

const Marker = styled.span`
  color: ${ink.soft};
  font-weight: normal;
`

// A célula comunica o estado por cor + ícone. Nenhum dos dois chega ao leitor de tela,
// daí o nome acessível por estado.
const CELL_LABELS = {
  ok: (row, col) => `${row} e ${col}: compatíveis`,
  bad: (row, col) => `${row} e ${col}: incompatíveis`,
  stale: (row, col) => `${row} e ${col}: divergência de cadastro`
}

// A diagonal é compatível como qualquer outra célula, mas "X e X: compatíveis" no
// leitor de tela seria ruído: o que interessa ali é que é o mesmo produto.
const cellLabel = (state, row, col) =>
  row.id === col.id
    ? `${row.name}: mesmo produto`
    : CELL_LABELS[state](row.name, col.name)

// O tooltip é renderizado num portal no <body> (ver render): dentro do Scroller ele
// era recortado pelo `overflow: auto`, e mesmo fora dele o modal recorta com
// `overflow: hidden`. Fora da árvore do modal, precisa vencer o z-index 9999 dele.
const Tooltip = styled(ReactTooltip)`
  z-index: 10001 !important;
  /* Largo de propósito: o balão cresce na horizontal antes de crescer na vertical, e
     nomes de produto são longos. Em tela estreita cede para a janela. */
  max-width: ${pxToRem(620)};
  white-space: normal;

  @media (max-width: ${pxToRem(720)}) {
    max-width: 90vw;
  }
`

// Sem max-width próprio: quem limita é o balão, senão haveria dois tetos de largura
// e o menor venceria.
const Tip = styled.div`
  text-align: left;
  font-weight: normal;
`

const TipTitle = styled.strong`
  display: block;
  margin-bottom: ${pxToRem(4)};
  font-size: ${pxToRem(13)};
  color: ${ink.strong};
`

const TipReason = styled.div`
  margin-top: ${pxToRem(5)};
  font-size: ${pxToRem(12)};
  line-height: 1.45;
`

const TipTag = styled.span`
  display: inline-block;
  margin-right: ${pxToRem(5)};
  padding: 0 ${pxToRem(5)};
  border-radius: ${pxToRem('3px')};
  background: ${ink.head};
  color: ${ink.strong};
  font-size: ${pxToRem(10)};
  letter-spacing: ${pxToRem('0.4px')};
  text-transform: uppercase;
`

const TipNote = styled.div`
  margin-top: ${pxToRem(6)};
  font-size: ${pxToRem(11)};
  color: ${ink.staleInk};
`

class IncompatibilityGridTable extends React.PureComponent {
  // react-tooltip v3 faz o bind no mount. Sem rebuild(), as células criadas depois de
  // uma troca de escopo não ganham tooltip. O projeto não usa rebuild() em lugar nenhum
  // hoje.
  componentDidMount() {
    ReactTooltip.rebuild()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.products !== this.props.products) {
      ReactTooltip.rebuild()
    }
  }

  // getContent devolve nó React: sem dangerouslySetInnerHTML, e uma única instância de
  // tooltip para a grade inteira em vez de uma por célula. O MOTIVO do conflito vem
  // junto - apontar a célula basta, sem precisar abrir o painel embaixo.
  renderTooltip = key => {
    if (!key) return null

    const { productsById, pairIndex } = this.props
    const [first, second] = key.split(':').map(Number)
    const nameOf = id => (productsById[id] ? productsById[id].name : '---')

    // Vale tanto para a célula da diagonal quanto para o cabeçalho da coluna, cujo
    // rótulo vertical é cortado quando o nome é longo.
    if (first === second) return <Tip>{nameOf(first)}</Tip>

    const pair = pairIndex[pairKey(first, second)]
    const title = `${nameOf(first)} × ${nameOf(second)}`

    if (!pair) {
      return (
        <Tip>
          <TipTitle>{title}</TipTitle>
          Compatíveis.
        </Tip>
      )
    }

    return (
      <Tip>
        <TipTitle>{title}</TipTitle>

        {pair.reasons && pair.reasons.length ? (
          pair.reasons.map((reason, index) => (
            <TipReason key={index}>
              <TipTag>{reason.label}</TipTag>
              {nameOf(reason.declared_by)} não pode conviver com <strong>{reason.name}</strong>,
              presente em {nameOf(reason.present_in)}.
            </TipReason>
          ))
        ) : (
          <TipReason>
            {pair.status === 'unknown'
              ? 'Motivo não carregado: o resultado foi truncado.'
              : 'Par bloqueado pelo índice do sistema, sem correspondência na regra atual.'}
          </TipReason>
        )}

        {pair.status === 'pending_refresh' && (
          <TipNote>
            Incompatível pelo cadastro atual, mas o índice usado pela validação ainda não
            foi atualizado.
          </TipNote>
        )}

        {pair.status === 'stale_block' && (
          <TipNote>
            O índice usado pela validação ainda bloqueia este par, mas o cadastro atual não
            o considera mais incompatível.
          </TipNote>
        )}
      </Tip>
    )
  }

  render() {
    const { products, productsById, pairIndex } = this.props

    return (
      <Scroller data-grid>
        <Grid headHeight={headerHeight(products)}>
          <thead>
            <tr>
              <CornerTh>Produto</CornerTh>
              {products.map(col => (
                <ColTh
                  key={col.id}
                  scope="col"
                  data-for={TOOLTIP_ID}
                  data-tip={pairKey(col.id, col.id)}
                >
                  <ColLabel>
                    {productsById[col.id] ? `${productsById[col.id].position}. ` : ''}
                    {col.name}
                  </ColLabel>
                </ColTh>
              ))}
            </tr>
          </thead>
          <tbody>
            {products.map(row => (
              <tr key={row.id}>
                <RowTh scope="row" title={row.name}>
                  {productsById[row.id] ? productsById[row.id].position : ''}. {row.name}
                  {!row.has_matrix && <Marker> *</Marker>}
                  {/* só ocorre no escopo de armário: o `active` da área é validado na
                      inclusão e nunca revisto, então o produto pode ter sido inativado
                      e continuar guardado ali. */}
                  {row.active_in_area === false && <Marker> (inativo na área)</Marker>}
                </RowTh>
                {products.map(col => {
                  const state = cellState(row, col, pairIndex)
                  const conflict = state !== 'ok'

                  // Só a célula em conflito ganha `data-tip`: o react-tooltip v3 liga
                  // três listeners em CADA elemento marcado no rebuild(), e fazer isso
                  // para as dezenas de milhares de células compatíveis custaria mais do
                  // que a informação vale - o cruzamento já é legível pelos dois eixos.
                  //
                  // A cor e o ícone não chegam ao leitor de tela, daí o nome acessível
                  // em toda célula (docs/12-acessibilidade-wcag-adequacao.md).
                  return (
                    <td
                      key={col.id}
                      className={CELL_CLASS[state]}
                      data-for={conflict ? TOOLTIP_ID : undefined}
                      data-tip={conflict ? pairKey(row.id, col.id) : undefined}
                      aria-label={cellLabel(state, row, col)}
                    />
                  )
                })}
              </tr>
            ))}
          </tbody>
        </Grid>

        {ReactDOM.createPortal(
          <Tooltip
            id={TOOLTIP_ID}
            place="bottom"
            type="light"
            effect="solid"
            className="exo-gpq-tooltip"
            getContent={this.renderTooltip}
          />,
          document.body
        )}
      </Scroller>
    )
  }
}

export default IncompatibilityGridTable



// WEBPACK FOOTER //
// ./src/containers/IncompatibilityAnalysis/IncompatibilityGridTable.js